import { useState } from 'react';
import { BriefingItemDto, BriefingGrade, OrderStatus } from '../../types/opsBriefing';
import { GRADE_LABEL } from './mockData';
import { ORDER_STATUS } from './MemberSummaryRow';
import { shortWon } from './tabData';
import { ValueTrigger } from './ValueBreakdownModal';
import CardIndexColumn from './CardIndexColumn';
import {
    beforeValueOf,
    keyMetricBeforeCovered,
    BEFORE_UNCOVERED_DELTA_TEXT,
} from './briefingComposition';
import { itemPassOrder, firstPassOfItem } from './adapters';
import { lookupChurnRate, DORMANT_GAP_DAYS, CHURN_RATE_BY_ORDER } from './passOrder';
import { isDormantItem } from './briefingComposition';

/**
 * 금액 축약은 화면 일입니다. 서버가 문자열로 내려주면 두 곳에서 갈립니다.
 * amount 하나만 받아 여기서 만듭니다.
 */
const shortAmountOf = (amount: number) => (amount > 0 ? `${shortWon(amount)} 원` : '');

/**
 * 브리핑 카드.
 *
 * 3영역 구조로 스캔 경로를 고정한다.
 *   [순번·등급] → [판단 문장 + 근거] → [핵심 지표 패널]
 *
 * 눈이 좌→우로 흐르면서 "누구 / 무슨 일 / 얼마나"가 순서대로 읽히도록 했다.
 * 설계 근거: DAILY_OPS_BRIEFING_UI_SPEC.md
 *
 * ─────────────────────────────────────────────────────────────
 * 한 벌로 목록과 상세를 다 그린다 (actions prop)
 * ─────────────────────────────────────────────────────────────
 * 한때 목록을 압축 요약 줄로 바꿨더니(BriefingSummaryRow) 숫자가 12px 로 눌려
 * "데이터를 크게 보는" 화면이 아니게 됐다. 대표 피드백으로 되돌린다 —
 * **목록은 이 카드의 데이터 영역 그대로**, 강사에게 요청하는 부분만 상세로 뺀다.
 *
 *   actions='none' 목록. 등급·이름·판단 문장·근거 스트립·우측 지표 패널까지 **데이터만**.
 *                  카드를 누르면 상세(BriefingDetailDrawer)가 열린다.
 *   actions='full' 상세. 위에 더해 오늘 할 일 배너·추천 문구·지시 버튼까지 전부.
 *
 * ⚠️ 카드를 두 벌로 포크하지 않는다. 목록용 카드를 따로 만들면 지표 표기를
 *    한쪽에서만 고치게 되고, 목록과 상세가 같은 회원을 다르게 설명한다.
 */

type GradeStyle = {
    accent: string; // 등급 대표색
    badgeBg: string; // 배지 배경
    tint: string; // 카드 좌측 영역 은은한 틴트
    rail: string; // 좌측 레일
};

const BORDER = '#E3EAF3';

const GRADE_STYLE: Record<BriefingGrade, GradeStyle> = {
    DANGER: { accent: '#FF3E4A', badgeBg: '#FFE8E9', tint: '#FFF7F7', rail: '#FF3E4A' },
    WARNING: { accent: '#B45309', badgeBg: '#FEF3C7', tint: '#FFFCF3', rail: '#F0A020' },
    CHANCE: { accent: '#3D73DD', badgeBg: '#ECF1FC', tint: '#F8FAFF', rail: '#3D73DD' },
    PRAISE: { accent: '#00A793', badgeBg: '#E5F8F3', tint: '#F5FDFA', rail: '#00A793' },
};

/**
 * 주차별 추이 막대.
 *
 * 선(스파크라인)보다 막대가 낫다고 판단했다 — 주간 수업 횟수는 이산값이고,
 * 막대는 "줄어드는 크기"가 면적으로 보여서 하락이 즉시 읽힌다.
 * 최근 3주는 강조색, 이전 5주는 회색으로 대비를 만든다.
 */
const TrendBars = ({
    data,
    color,
    alert,
    compact = false,
}: {
    data: number[];
    color: string;
    alert?: boolean;
    /**
     * 목록 모드에서 쓰는 반높이 막대.
     * 막대 규칙(최근 3주 강조)은 그대로 두고 높이만 줄인다 — 규칙이 자리마다
     * 다르면 같은 회원의 추이가 목록과 상세에서 다르게 읽힌다.
     */
    compact?: boolean;
}) => {
    const max = Math.max(...data, 1);
    const H = compact ? 24 : 44;

    return (
        <div>
            <div className="flex items-end" style={{ height: H, gap: compact ? 3 : 4 }}>
                {data.map((v, i) => {
                    const isRecent = i >= data.length - 3;
                    const h = Math.max((v / max) * H, 3);
                    return (
                        <div
                            key={i}
                            style={{
                                flex: 1,
                                height: h,
                                borderRadius: 3,
                                background: isRecent ? color : '#DDE5F0',
                                opacity: isRecent ? 1 : 0.85,
                            }}
                        />
                    );
                })}
            </div>
            <div className="flex justify-between" style={{ marginTop: compact ? 4 : 6 }}>
                <span style={{ fontSize: compact ? 10.5 : 11, color: '#B4BDC8' }}>8주 전</span>
                <span
                    style={{
                        fontSize: compact ? 10.5 : 11,
                        color: alert ? color : '#B4BDC8',
                        fontWeight: 600,
                    }}
                >
                    이번 주
                </span>
            </div>
        </div>
    );
};

/**
 * 금액 태그 — 시선을 잡되 토스 스타일을 해치지 않게.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 두 숫자를 **한 칸에 더하지 않습니다** (2026-08-31 대표 확정본)
 * ─────────────────────────────────────────────────────────────
 * 예전에는 태그가 하나였고 라벨이 「예상 손실 방어」였습니다. 그 한 숫자에
 * 「이미 받은 선수금」과 「앞으로 받을 기댓값」이 섞여 있었습니다 — 성격이
 * 다른 돈이라 더할 수 없는데 더해 놓고, 라벨은 둘 중 한쪽만 말했습니다.
 *
 * 이제 있는 것만 각각 붙입니다. 이탈 위험·휴면 카드는 기대가 0 이라
 * **자연히 선수금 하나만** 남습니다 — 그 카드가 예전에 「예상 손실 방어」라고
 * 부르던 금액이 실제로는 미사용 선수금이었다는 사실이 이제 이름에 드러납니다.
 */
const VALUE_TONE = {
    PREPAID: { bg: '#FFF0F0', border: '#FBD0D3', color: '#D92D3A', icon: '🚨', label: '미사용 선수금' },
    EXPECTED: { bg: '#EAF7F2', border: '#B7DFD2', color: '#00795F', icon: '💸', label: '재등록 기대' },
} as const;

const ValueTag = ({
    value,
    subjectName,
    onShow,
}: {
    value: BriefingItemDto['value'];
    /** 스크린리더가 「누구의 금액인가」를 알 수 있게 */
    subjectName: string;
    /** 없으면 예전처럼 글자로만 둔다 (상세 미리보기 등) */
    onShow?: (axis: 'PREPAID' | 'EXPECTED') => void;
}) => {
    if (!value) return null;
    /*
     * 0원 칸은 안 그린다. 「재등록 기대 0원」은 이탈 위험 카드에서는 **정책상 당연한
     * 값**이라(기대하지 않기로 한 자리) 매번 띄우면 경고처럼 읽힌다.
     * 왜 0 인지는 breakdown 툴팁이 말한다.
     */
    const chips = [
        value.prepaid && value.prepaid > 0
            ? { ...VALUE_TONE.PREPAID, n: value.prepaid, axis: 'PREPAID' as const }
            : null,
        value.expected && value.expected > 0
            ? { ...VALUE_TONE.EXPECTED, n: value.expected, axis: 'EXPECTED' as const }
            : null,
    ].filter(Boolean) as Array<{
        bg: string;
        border: string;
        color: string;
        icon: string;
        label: string;
        n: number;
        axis: 'PREPAID' | 'EXPECTED';
    }>;
    if (chips.length === 0) return null;

    return (
        <span className="inline-flex items-center" style={{ gap: 6, flex: 'none' }}>
            {chips.map((t) => {
                const inner = (
                    <>
                        <span style={{ fontSize: 13 }}>{t.icon}</span>
                        <span style={{ fontSize: 13, color: t.color }}>{t.label}</span>
                        <span
                            style={{
                                fontSize: 16,
                                fontWeight: 800,
                                color: t.color,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            {t.n.toLocaleString('ko-KR')}원
                        </span>
                    </>
                );
                const box: React.CSSProperties = {
                    background: t.bg,
                    border: `1px solid ${t.border}`,
                    padding: '6px 12px',
                    gap: 7,
                    /* 숫자와 「원」이 갈라지지 않게 — 금액은 한 덩어리로 읽혀야 한다 */
                    whiteSpace: 'nowrap',
                    flex: 'none',
                };
                /*
                  🔴 금액 태그가 **눌러진다** (2026-09-01 QA31).
                     누르면 「잔여 15회 × 55,000원 = 825,000원」이 뜬다.
                     칩 자체가 이미 눌러 보이므로 밑줄은 안 붙인다.
                     `onShow` 를 안 받은 자리(상세 미리보기 등)는 예전 그대로 글자다.
                */
                return onShow ? (
                    <ValueTrigger
                        key={t.label}
                        label={`${subjectName} ${t.label} 산출 근거 보기`}
                        onClick={() => onShow(t.axis)}
                        underline={false}
                        className="inline-flex items-center rounded-[8px]"
                        style={box}
                    >
                        {inner}
                    </ValueTrigger>
                ) : (
                    <span
                        key={t.label}
                        className="inline-flex items-center rounded-[8px]"
                        title={value.breakdown}
                        style={{ ...box, cursor: 'help' }}
                    >
                        {inner}
                    </span>
                );
            })}
        </span>
    );
};

/**
 * 버튼에 붙는 한 조각 — **두 숫자를 더하지 않고 하나만 고른다.**
 *
 * 기대가 있으면 그쪽(지금 움직여서 새로 생기는 돈), 없으면 선수금.
 * 버튼 위에서 둘을 다 보여주면 글자가 넘쳐 줄이 접힌다.
 */
const buttonValueOf = (
    value: BriefingItemDto['value']
): { text: string; word: string } | null => {
    if (!value) return null;
    if (value.expected && value.expected > 0) {
        const t = shortAmountOf(value.expected);
        return t ? { text: t, word: '확보' } : null;
    }
    if (value.prepaid && value.prepaid > 0) {
        const t = shortAmountOf(value.prepaid);
        return t ? { text: t, word: '선수금' } : null;
    }
    return null;
};

/**
 * 액션마다 "이 메시지가 누구에게 가고, 누르면 무슨 일이 생기는지"가 다르다.
 * 전부 "강사에게 전달"이라고 써두면, 대표가 직접 전화해야 하는 건까지
 * 강사가 알아서 하는 줄 알고 넘어간다.
 *
 * ⚠️ 문구는 **서버가 실제로 하는 일**까지만 약속해야 한다.
 * 한때 "누르면 위 메시지가 강사 앱으로 바로 갑니다"라고 써 놓고 실제로는 기록만
 * 남던 시절이 있었고, 그래서 "알림은 아직 나가지 않습니다"로 내려 적었다.
 * 2026-08-26 부터는 운영에서 푸시가 실제로 나간다(OPS_ORDER_PUSH_ENABLED=true).
 * 안 나간다고 적힌 화면을 그대로 두면, 원장님은 이미 알림을 받은 강사에게
 * 같은 말을 한 번 더 전화로 한다.
 *
 * ⚠️ 다만 웹은 푸시가 켜졌는지 **확정할 수 없다** — 서버가 알려주는 API 가 없다.
 *    자세한 것은 OrderConfirmModal 의 COACH_PUSH_NOTICE 주석에 적어 두었다.
 *    그래서 "전송됩니다"까지만 말하고, 받았다고 단정하지 않는다.
 *
 * willCreateOrder 는 이 항목이 실제로 지시로 남는지 여부다.
 * 회원 항목 + 담당 강사가 붙어 있을 때만 지시가 만들어진다
 * (OpsBriefingTodayPage.canCreateOrder).
 */
const outcomeOf = (item: BriefingItemDto, willCreateOrder: boolean) => {
    const coach = item.relatedCoachName ?? '담당 강사';
    const pushGoesOut = (
        <span style={{ color: '#2C4C8C' }}>강사 앱으로 푸시 알림이 전송됩니다.</span>
    );
    /*
     * 면담·칭찬은 **지시를 만들지 않는다** (canCreateOrder 는 회원 항목 + 담당 강사일 때만 참).
     * 지시가 없으면 알림도 없다 — 푸시가 켜진 것과 무관하다. 여기에 "푸시가 갑니다"를
     * 적으면 새로운 거짓말이 된다. 그래서 이 두 갈래는 여전히 직접 전해야 한다.
     */
    const recordOnly = (
        <span style={{ color: '#7C8794' }}>
            강사 앱으로는 가지 않는 기록입니다 — 내용은 직접 전해 주세요.
        </span>
    );

    switch (item.recommendation.actionCode) {
        case 'CALL':
            return {
                toLabel: `${item.subjectName} 회원에게 할 이야기`,
                effect: (
                    <>
                        <b style={{ color: '#C22D38' }}>대표님이 직접 연락하시는 건입니다.</b> 강사를 거치면 늦습니다.
                        <br />
                        통화 후 버튼을 누르면 이 항목이 <b style={{ color: '#5B6B7F' }}>처리 완료</b>로 기록됩니다.
                    </>
                ),
            };
        case 'MEETING':
            return {
                toLabel: `${item.subjectName} 강사에게 할 이야기`,
                effect: (
                    <>
                        누르면 면담 요청을 하셨다는 기록이 남습니다.
                        <br />
                        {recordOnly}
                    </>
                ),
            };
        case 'PRAISE':
            return {
                toLabel: `${item.subjectName} 강사에게 할 이야기`,
                effect: (
                    <>
                        누르면 칭찬을 전하셨다는 기록이 남습니다. 잘한 일은 바로 말해줄수록 다음 달에 다시 나옵니다.
                        <br />
                        {recordOnly}
                    </>
                ),
            };
        default:
            return {
                toLabel: `${coach} 강사에게 전할 내용`,
                effect: willCreateOrder ? (
                    <>
                        누르면 {coach} 강사 앞으로 지시가 만들어져 아래{' '}
                        <b style={{ color: '#5B6B7F' }}>진행 현황</b>에 남고, 실행 여부를 추적합니다.
                        <br />
                        {pushGoesOut}
                    </>
                ) : (
                    <>
                        누르면 이 항목이 <b style={{ color: '#5B6B7F' }}>처리 완료</b>로 기록됩니다.
                        <br />
                        담당 강사가 지정되지 않아 지시로는 남지 않습니다.
                    </>
                ),
            };
    }
};

type Props = {
    item: BriefingItemDto;
    /**
     * 카드 왼쪽에 찍는 순번 — **표시용입니다.**
     *
     * ⚠️ 완료 기록 API 가 쓰는 서버 배열 위치(index)와 혼동하지 마세요.
     *    그 위치는 화면이 아니라 OpsBriefingTodayPage 가 정렬 전 items 에서
     *    findIndex 로 구합니다. 여기 들어오는 값은 "오늘 목록의 몇 번째"입니다.
     */
    index: number;
    /** 이 항목이 실제 지시(order)로 남는가 — 문구가 약속할 범위를 정한다 */
    willCreateOrder: boolean;
    /** draft 는 원장이 고친 문구. "고친 내용 그대로 전달됩니다"를 지키려면 함께 올려야 한다 */
    onAction: (itemId: string, actionCode: string, draft: string) => void;
    /**
     * 완료로만 기록한다. **지시를 만들지 않는다.**
     *
     * ⚠️ onAction 과 절대 같은 함수를 넘기지 마세요.
     * 예전에는 왼쪽 체크박스(title="완료로 표시")가 주 CTA 와 같은 onAction 을 불렀습니다.
     * 원장님은 "이건 내가 이미 처리했다"는 뜻으로 네모를 눌렀는데,
     * 그때마다 강사 앞으로 지시가 새로 만들어져 진행 현황에 쌓였습니다.
     * 이미 끝난 일을 강사에게 시키는 지시였습니다.
     */
    onMarkDone: (itemId: string) => void;
    /** 완료 처리를 취소한다 */
    onUndo: (itemId: string) => void;
    /** 근거·지표 클릭 시 원천 데이터 팝업 */
    onShowRaw: (itemId: string) => void;
    /**
     * 금액 태그를 눌렀다 — 이 건의 산출 근거 팝업을 연다 (2026-09-01 QA31).
     *
     * ⚠️ 창은 **페이지가** 띄웁니다. 이 카드는 상세(2300) 안에서도 그려지는데,
     *    카드가 직접 띄우면 창 안의 창이 되어 Esc 한 번에 둘 다 닫힙니다.
     *    안 넘기면 금액 태그는 예전처럼 그냥 글자입니다.
     */
    onShowValue?: (itemId: string, axis: 'PREPAID' | 'EXPECTED') => void;
    /**
     * 서버 왕복 중인가.
     * 버튼을 잠그지 않으면 연타로 같은 지시가 두 건 생깁니다 — 멱등이 아닙니다.
     */
    busy?: boolean;
    /**
     * 이 회원 건으로 **이미 나가 있는 지시**.
     *
     * 성공 토스트는 2.6초 뒤 사라집니다. 그러면 원장님은 보냈는지 안 보냈는지
     * 알 방법이 없어 진행 현황을 따로 열어봐야 했습니다.
     * 카드에 그대로 남겨서, 화면만 보고도 "이건 이미 시켰다"가 읽히게 합니다.
     *
     * status 는 회원 현황 목록과 **같은 표**(ORDER_STATUS)로 배지를 그리는 데 씁니다.
     * "지시함" 하나로 뭉개면 아직 안 본 지시와 이미 끝난 지시가 같아 보입니다.
     */
    sentOrder?: {
        coachName: string;
        sentLabel: string;
        task: string;
        status?: OrderStatus;
    } | null;
    /**
     * 강사에게 요청하는 부분을 그릴 것인가 (파일 상단 주석 참고).
     *   'full' 상세 — 오늘 할 일 배너·추천 문구·지시 버튼까지 전부 (기본값)
     *   'none' 목록 — 데이터만. 요청은 카드를 눌러 상세에서 한다
     */
    actions?: 'full' | 'none';
    /**
     * 목록 모드에서 카드를 눌렀다 — 상세를 연다.
     * 없으면 카드는 눌리지 않는다 (상세 안에서 자기 자신을 또 열면 안 된다).
     */
    onOpenDetail?: (itemId: string) => void;
    /**
     * 가리기 — 목록에서 내린다 (서버에 DISMISSED 로 남는다).
     * 없으면 버튼을 그리지 않는다. 상세 안에서는 넘기지 않는다 —
     * 보고 있는 카드를 가리면 빈 드로어가 남는다.
     */
    onHide?: (itemId: string) => void;
    /** 서버 왕복 중이라 목록 전체가 잠겼는가 (가리기 버튼) */
    locked?: boolean;
    /** 상세가 열려 있는 카드인가 — 목록에서 어느 것을 열었는지 표시한다 */
    selected?: boolean;
    /**
     * 「선물 보내기」 버튼의 상태.
     *
     * 이 버튼만 조건이 다릅니다 — **포인트가 나가는 동작**이라
     *   · 보낼 수 없는 계정(센터 열람·슈퍼관리자)에서는 아예 그리지 않고
     *   · 지난 날짜 브리핑에서는 눌리지 않게 잠급니다.
     * 넘기지 않으면 서버가 준 그대로 그립니다.
     */
    giftGate?: { hidden?: boolean; disabled?: boolean; reason?: string };
    /**
     * 이 회원에게 이번에 보낸 선물 — **문장 그대로** 받는다.
     *
     * 상품 이름을 여기서 조립하지 않는다. 서버가 기존 배치를 그대로 돌려준 경우
     * (같은 날 두 번째 시도) 실제로 나간 것은 이번에 고른 상품이 아니라서,
     * 화면이 이름을 만들면 거짓말이 된다. 무엇을 말할지는 발송 쪽이 정한다.
     */
    sentGift?: string | null;
};

const BriefingCard = ({
    item,
    index,
    willCreateOrder,
    onAction,
    onMarkDone,
    onUndo,
    onShowRaw,
    onShowValue,
    busy = false,
    sentOrder = null,
    actions = 'full',
    onOpenDetail,
    onHide,
    locked = false,
    selected = false,
    giftGate,
    sentGift = null,
}: Props) => {
    const s = GRADE_STYLE[item.grade];
    // 문구를 고쳐 보낼 수 있게 한다. 예전에는 '수정해서 보내기'가 곧바로 완료 처리돼 버렸다.
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(item.recommendation.draft);
    const [hoverCheck, setHoverCheck] = useState(false);
    const [hoverCard, setHoverCard] = useState(false);
    const suffix = item.subjectType === 'MEMBER' ? ' 회원' : ' 강사';
    /*
     * 첫 수강권인가 — 순번 원본이 왔을 때만 안다 (대표 결정 ③).
     * 판정은 `adapters` 한 곳에서만 한다. 여기서 세면 회원 현황의 순번 배지와 갈린다.
     */
    /*
     * 순번 라벨·이탈률 문구의 원천 — **웹이 계속 계산합니다.**
     * 서버가 주는 것은 불리언 하나뿐이고, 라벨까지 서버가 만들면
     * 회원 현황의 순번 배지와 갈립니다. 없으면 null 이고 툴팁만 짧아집니다.
     */
    const passOrder = itemPassOrder(item);
    /*
     * 🔴 배지 판정은 **정렬과 같은 함수**를 봅니다 (`adapters.firstPassOfItem`).
     *    서버가 `isFirstPass` 를 실었으면 그 값이 이기고, 없으면 자기 계산으로
     *    떨어집니다. 여기서 따로 계산하면 「첫 수강권」이라고 **안 적힌** 카드가
     *    첫 수강권이라는 이유로 위에 서는 모순이 생깁니다 —
     *    박제 시점(그날 아침)과 지금 응답의 `memberships[]` 가 다를 수 있어서
     *    실제로 갈립니다.
     */
    const firstPass = firstPassOfItem(item);
    const outcome = outcomeOf(item, willCreateOrder);
    /** 목록 모드 — 데이터만 그리고, 카드 전체가 상세로 가는 문이 된다 */
    const listMode = actions === 'none';
    const openable = listMode && !!onOpenDetail;
    const orderBadge = sentOrder?.status ? ORDER_STATUS[sentOrder.status] : null;

    /**
     * 카드 전체를 누르면 상세가 열린다.
     *
     * ⚠️ 안쪽 버튼(완료 체크·가리기·근거 보기)은 전부 전파를 막는다.
     *    안 막으면 가리기를 누를 때마다 상세까지 함께 열린다.
     */
    const openProps = openable
        ? {
              role: 'button',
              tabIndex: 0,
              'aria-label': `${item.subjectName}${suffix} 시그널 상세 보기`,
              'aria-expanded': selected,
              onClick: () => onOpenDetail?.(item.itemId),
              onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOpenDetail?.(item.itemId);
                  }
              },
              onMouseEnter: () => setHoverCard(true),
              onMouseLeave: () => setHoverCard(false),
          }
        : {};
    /*
     * 이전 구간이 회원 등록 이후를 덮는가.
     * 서버가 이 사실을 안 보내면 true — 예전과 똑같이 그린다.
     * 판정은 briefingComposition 한 곳에만 둔다 (요약 줄과 카드가 같은 말을 해야 한다).
     */
    const beforeCovered = keyMetricBeforeCovered(item.keyMetric);

    // 처리 완료 — 사라지지 않고 접힌다 (성취감 + 실수 복구)
    if (item.status === 'ACTED') {
        return (
            <div
                className="flex w-full items-center gap-[12px] rounded-[14px] border border-solid border-[#E3EAF3] bg-[#FAFCFF] px-[24px] py-[16px]"
                style={{ cursor: openable ? 'pointer' : 'default' }}
                {...openProps}
            >
                <span
                    className="flex items-center justify-center rounded-full"
                    style={{ width: 22, height: 22, background: '#00A793', color: '#fff', fontSize: 13, fontWeight: 700 }}
                >
                    ✓
                </span>
                <span style={{ fontSize: 17, fontWeight: 700, color: '#7C8794' }}>
                    {item.subjectName}
                    {suffix}
                </span>
                <span style={{ fontSize: 16, color: '#A7B0BC' }}>{item.title}</span>
                <span className="ml-auto flex items-center" style={{ gap: 14 }}>
                    {/*
                      "처리 완료" 하나로 뭉개면 두 가지가 구분되지 않는다 —
                      강사에게 지시를 낸 건과, 원장이 직접 처리하고 기록만 남긴 건.
                      지시를 낸 건은 강사에게 직접 전할 일이 아직 남아 있다.
                    */}
                    {sentOrder ? (
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#00A793' }}>
                            {sentOrder.coachName} 강사에게 지시함 · {sentOrder.sentLabel}
                        </span>
                    ) : (
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#00A793' }}>처리 완료</span>
                    )}
                    {/*
                      체크박스로 한 번에 완료되니 잘못 누를 수 있다. 되돌릴 길을 열어둔다.

                      ⚠️ 목록에는 두지 않는다. 되돌리기는 "이 건을 다시 할 일로 만든다"는
                      뜻이라, 훑어보는 중에 스치듯 눌릴 자리에 있으면 안 된다.
                      카드를 누르면 열리는 상세에 그대로 있다.
                    */}
                    {!listMode && (
                        <button
                            type="button"
                            onClick={() => onUndo(item.itemId)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: 13,
                                color: '#93A0AE',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                padding: 0,
                            }}
                        >
                            되돌리기
                        </button>
                    )}
                </span>
            </div>
        );
    }

    return (
        <div
            className="flex w-full overflow-hidden rounded-[14px] border border-solid bg-white"
            style={{
                borderColor: selected ? s.rail : '#E3EAF3',
                boxShadow: selected
                    ? `0 0 0 3px ${s.badgeBg}`
                    : hoverCard
                      ? '0 6px 18px rgba(16,32,64,0.10)'
                      : '0 1px 2px rgba(16,32,64,0.04)',
                cursor: openable ? 'pointer' : 'default',
                transition: 'box-shadow .15s, border-color .15s',
            }}
            {...openProps}
        >
            {/*
              순번 + 체크박스 — **과제 카드와 같은 컴포넌트**를 쓴다 (QA35).
              두 카드가 한 벌의 목록이라 왼쪽 열이 갈리면 번호가 어긋난다.

              ⚠️ 이 네모는 **완료 기록만** 한다. 지시를 만들지 않는다.
              예전에는 주 CTA 와 같은 onAction 을 불러서, "이미 내가 처리했다"는 뜻으로
              네모를 누를 때마다 강사 앞으로 지시가 새로 만들어졌다.
              지시를 만드는 것은 오른쪽 주 버튼 하나뿐이다.
            */}
            <CardIndexColumn
                rail={s.rail}
                tint={s.tint}
                accent={s.accent}
                index={index}
                paddingTop={listMode ? 15 : 24}
            >
                <button
                    type="button"
                    onClick={(e) => {
                        // 목록에서는 카드 전체가 상세로 가는 문이다. 여기서 막지 않으면
                        // 완료로 표시하면서 상세까지 함께 열린다.
                        e.stopPropagation();
                        // 누르는 순간 카드가 접힘 상태로 바뀌어 onMouseLeave 가 오지 않는다.
                        // 되돌렸을 때 체크가 남아 있지 않도록 여기서 직접 끈다.
                        setHoverCheck(false);
                        onMarkDone(item.itemId);
                    }}
                    onMouseEnter={() => setHoverCheck(true)}
                    onMouseLeave={() => setHoverCheck(false)}
                    disabled={busy}
                    title="완료로 표시 (지시는 만들지 않습니다)"
                    aria-label={`${item.subjectName}${suffix} 건 완료로 표시`}
                    className="flex items-center justify-center"
                    style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        background: hoverCheck ? '#00A793' : '#FFFFFF',
                        border: hoverCheck ? '1px solid #00A793' : `2px solid ${s.rail}`,
                        color: '#FFFFFF',
                        fontSize: 19,
                        fontWeight: 800,
                        cursor: 'pointer',
                        transition: 'background .15s, border-color .15s',
                        padding: 0,
                    }}
                >
                    {hoverCheck ? '✓' : ''}
                </button>
            </CardIndexColumn>

            {/*
              본문.
              목록에서는 위아래 여백을 줄인다 — 이 칸은 제목 + 수치 스트립에서
              끝나는데 예전 여백(22/20)은 지시 블록이 아래 붙어 있던 시절의 값이다.
            */}
            <div
                className="flex min-w-0 flex-1 flex-col"
                style={{ padding: listMode ? '14px 22px 14px 8px' : '22px 24px 20px 8px' }}
            >
                {/* 대상 */}
                {/*
                  🔴 좁은 폭에서 **배지가 이름을 밀지 않게** 한다 (2026-08-31 QA12).
                     `flexWrap: 'wrap'` 이 없으면 배지들이 이름 칸을 눌러서
                     「이은수 회 / 원」처럼 이름이 중간에서 꺾인다.
                     이름을 줄이는 대신 배지를 아랫줄로 내린다 — 누구 건인지가
                     배지보다 먼저 읽혀야 한다.
                */}
                <div className="flex items-center gap-[10px]" style={{ flexWrap: 'wrap' }}>
                    <span
                        className="rounded-[6px]"
                        style={{
                            background: s.badgeBg,
                            color: s.accent,
                            fontSize: 12.5,
                            fontWeight: 800,
                            padding: '4px 10px',
                            letterSpacing: '0.02em',
                        }}
                    >
                        {GRADE_LABEL[item.grade]}
                    </span>
                    {/*
                      목록에서는 이름이 버튼이 아니다.
                      카드 전체가 상세로 가는 문이라, 이름만 원천 데이터로 새면
                      같은 카드 안에서 누르는 자리마다 다른 화면이 열린다.
                    */}
                    {listMode ? (
                        <span
                            style={{
                                fontSize: 17,
                                fontWeight: 700,
                                color: '#16202E',
                                /* 「회원」이 「회 / 원」으로 갈라지지 않게 */
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.subjectName}
                            {suffix}
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onShowRaw(item.itemId)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                cursor: 'pointer',
                                fontSize: 17,
                                fontWeight: 700,
                                color: '#16202E',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.subjectName}
                            {suffix}
                        </button>
                    )}
                    {/*
                      첫 수강권 표기 (2026-08-27 대표 결정 ③).

                      🔴 **카드가 뜬 이유가 아닙니다.** 이 카드는 기존 문제 신호
                         (공백·페이스·골든타임·당일 취소)로 떠 있고, 이 배지는
                         "그 문제를 첫 수강권 회원이 겪고 있다"만 덧붙입니다.
                         그래서 판단 문장(item.title)을 건드리지 않고 이름 옆에 붙습니다.

                      회원 현황의 순번 배지와 **같은 색 규칙**입니다(1번째만 붉게).
                      다만 글자는 「1번째」가 아니라 「첫 수강권」으로 적습니다 —
                      카드 왼쪽에 이미 카드 번호(1, 2, 3 …)가 크게 있어서
                      「1번째」라고 쓰면 그 번호와 겹쳐 읽힙니다.

                      이탈률 문구는 lookupChurnRate 에서 가져옵니다.
                      숫자를 여기 적지 마세요 — 원천은 CHURN_RATE_BY_ORDER 하나입니다
                      (passOrder.ts 의 FIRST_PASS_CHURN_RATE 주석).
                    */}
                    {firstPass && (
                        <span
                            className="rounded-[5px]"
                            /*
                              🔴 `passOrder` 가 **null 일 수 있습니다.**
                                 서버가 `isFirstPass: true` 를 실었는데 순번 원본
                                 (`membership.memberships[]`)이 안 온 항목이 그렇습니다.
                                 예전에는 배지 조건이 곧 `passOrder != null` 이라
                                 `!` 로 단정해도 됐지만, 이제 판정 근거가 둘입니다.
                                 여기서 터지면 카드가 통째로 안 그려집니다.
                                 근거를 모르면 **툴팁만 짧아지고** 배지는 그대로 답니다.
                            */
                            title={
                                passOrder
                                    ? `${passOrder.reason}\n${lookupChurnRate(passOrder).text}`
                                    : `첫 수강권 구간입니다 · 이탈률 ${CHURN_RATE_BY_ORDER[1]}%`
                            }
                            style={{
                                background: '#FFE8E9',
                                color: '#D92D3A',
                                fontSize: 11.5,
                                fontWeight: 800,
                                padding: '3px 8px',
                                whiteSpace: 'nowrap',
                                flex: 'none',
                                cursor: 'help',
                            }}
                        >
                            첫 수강권
                        </span>
                    )}
                    {/*
                      휴면 표기 (2026-08-27 백엔드 2차 · 서버 `dormant-` 항목).

                      🔴 **빨강을 쓰지 않습니다. 회색입니다.**
                         이 카드의 회원은 공백이 45일을 넘겼습니다 — 대개 60일 이상입니다.
                         거기에 긴급한 어조를 얹으면 원장님이 오늘 당장 전화를 걸게 되는데,
                         두 달을 안 나온 회원에게 오늘의 전화는 이미 늦은 얘기이고
                         "왜 이제야" 로 읽힙니다. 여기서 할 일은 복귀 확인이지 이탈 방어가
                         아니라서, 위험(빨강)과 **눈으로 구분되어야** 합니다.

                      등급 배지는 그대로 「주의」(WARNING) 입니다. 이 배지는 그 옆에서
                      "같은 주의라도 성격이 다르다" 만 덧붙입니다 — 첫 수강권 배지가
                      카드가 뜬 이유를 바꾸지 않는 것과 같은 자리입니다.
                    */}
                    {isDormantItem(item) && (
                        <span
                            className="rounded-[5px]"
                            title={`마지막 수업이 ${DORMANT_GAP_DAYS}일 넘게 지난 회원입니다\n이탈 방어가 아니라 복귀 확인을 하는 자리입니다`}
                            style={{
                                background: '#EEF1F5',
                                color: '#5B6B7F',
                                fontSize: 11.5,
                                fontWeight: 800,
                                padding: '3px 8px',
                                whiteSpace: 'nowrap',
                                flex: 'none',
                                cursor: 'help',
                            }}
                        >
                            휴면
                        </span>
                    )}
                    {/*
                      「지시함 · N일째」 (2026-08-27 백엔드 3차).

                      지시한 항목은 가리기와 달리 **다음 날에도 그대로 뜹니다** —
                      강사가 안 움직였는데 사흘간 사라지면 그게 더 위험해서입니다.
                      그래서 원장님은 어제 시킨 카드를 오늘 또 봅니다. 이 배지가
                      그 자리에 답합니다: 「이미 시켰고 오늘로 N일째」.

                      🔴 중성 톤입니다. 붉게 칠하면 「지시가 잘못됐다」로 읽히는데,
                         이 배지가 말하는 것은 경과일뿐입니다. 판단은 원장님이 합니다.

                      ⚠️ `status === 'ACTED'` 로 대신 추론하지 마세요. 지난 리포트의
                         완료 토글도 ACTED 를 만들지만 그건 나간 지시가 아닙니다
                         (서버가 `orderId` 있는 건만 셉니다 — 타입 주석 참고).
                      ⚠️ 0(오늘 나감)은 그리지 않습니다. 「0일째」는 말이 안 됩니다.
                    */}
                    {item.lastOrderedDaysAgo != null && item.lastOrderedDaysAgo > 0 && (
                        <span
                            className="rounded-[5px]"
                            title={`마지막 지시가 ${item.lastOrderedDaysAgo}일 전에 나갔습니다\n지시한 항목은 가려지지 않아 다음 날에도 계속 보입니다`}
                            style={{
                                background: '#EEF1F5',
                                color: '#5B6B7F',
                                fontSize: 11.5,
                                fontWeight: 800,
                                padding: '3px 8px',
                                whiteSpace: 'nowrap',
                                flex: 'none',
                                cursor: 'help',
                            }}
                        >
                            지시함 · {item.lastOrderedDaysAgo}일째
                        </span>
                    )}
                    {item.relatedCoachName && (
                        <span style={{ fontSize: 15, color: '#93A0AE' }}>
                            담당 {item.relatedCoachName} 강사
                        </span>
                    )}
                    <span className="ml-auto flex items-center" style={{ gap: 10 }}>
                        <ValueTag
                            value={item.value}
                            subjectName={item.subjectName}
                            onShow={
                                onShowValue
                                    ? (axis) => onShowValue(item.itemId, axis)
                                    : undefined
                            }
                        />
                        {/*
                          이미 나가 있는 지시. 목록에서는 이 배지가 "또 시켜야 하나"에
                          답한다 — 상세를 열지 않고도 읽혀야 한다.
                          (상세에는 버튼 위 안내가 같은 사실을 더 길게 말한다)
                        */}
                        {listMode && orderBadge && (
                            <span
                                className="rounded-[6px]"
                                style={{
                                    background: orderBadge.bg,
                                    color: orderBadge.color,
                                    fontSize: 12,
                                    fontWeight: 800,
                                    padding: '4px 9px',
                                    whiteSpace: 'nowrap',
                                    flex: 'none',
                                }}
                                title={
                                    sentOrder
                                        ? `${sentOrder.sentLabel}에 ${sentOrder.coachName} 강사에게 지시하셨습니다`
                                        : undefined
                                }
                            >
                                {orderBadge.label}
                            </span>
                        )}
                        {/*
                          가리기 — "이 데이터는 안 보이게 해 주세요".
                          카드 우상단에 둔다. 서버에 DISMISSED 로 남고,
                          빈 자리는 편성 함수가 다음 위험 순위로 채운다.

                          ⚠️ **「오늘 하루」가 아니라 「사흘간」이다** (백엔드 3차).
                             예전에는 생성 배치가 어제 문서를 안 읽어서 가린 항목이
                             다음 날 되살아났다. 이제 사흘간 억제된다 — 원장이
                             "내일 또 뜨겠지" 로 읽으면 안 되므로 툴팁이 기간을 말한다.
                             (그 사이 등급이 올라가면 예외로 다시 올라온다.
                              「영영 안 뜬다」로 읽히지 않게 단정하지 않는다)
                        */}
                        {listMode && onHide && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    /* 이 버튼은 상세를 여는 것이 아니라 목록에서 내린다 */
                                    e.stopPropagation();
                                    onHide(item.itemId);
                                }}
                                disabled={locked}
                                aria-disabled={locked}
                                /*
                                  🔴 접근성 이름(aria-label)은 **짧게 둔다.**
                                     여기에 기간 안내까지 넣으면 포커스할 때마다
                                     긴 문장이 낭독된다. 보충 설명은 title 이 맡는다
                                     (title 은 접근성 '설명'으로 읽힌다).
                                */
                                aria-label={`${item.subjectName}${suffix} 건 가리기`}
                                title={`${item.subjectName}${suffix} 건 가리기\n사흘간 다시 알리지 않습니다`}
                                className="rounded-[8px]"
                                style={{
                                    flex: 'none',
                                    height: 30,
                                    padding: '0 11px',
                                    background: '#FFFFFF',
                                    border: '1px solid #DCE4EE',
                                    color: locked ? '#B4BDC8' : '#5B6B7F',
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    cursor: locked ? 'progress' : 'pointer',
                                }}
                            >
                                가리기
                            </button>
                        )}
                    </span>
                </div>

                {/* 판단 문장 — 시각적 주인공 */}
                <div style={{ marginTop: listMode ? 9 : 12 }}>
                    <span style={{ fontSize: 23, fontWeight: 700, color: '#0F1723', lineHeight: '32px', letterSpacing: '-0.02em' }}>
                        {item.title}
                    </span>
                </div>

                {/*
                  근거 수치.
                  예전에는 작은 알약 칩이라 판단 문장에 묻혔다.
                  이 숫자가 곧 판단의 근거인데 안 보이면 카드를 믿을 이유가 없다.
                  그래서 칸을 나눈 지표 스트립으로 키우고, 눌러서 원천 데이터로 이어지게 한다.

                  ⚠️ 근거가 하나도 없는 항목은 상자 자체를 그리지 않는다. 예전에는 빈
                  테두리만 남아서, 원장님이 "여기 숫자가 안 뜬다"고 읽었다 —
                  실제로는 서버가 그 항목에 근거를 안 붙인 것이다.
                */}
                {item.facts.length > 0 && (
                <div
                    className="flex w-full items-stretch rounded-[12px]"
                    style={{
                        marginTop: listMode ? 11 : 16,
                        border: `1px solid ${BORDER}`,
                        background: '#FCFDFE',
                        overflow: 'hidden',
                    }}
                >
                    {item.facts.map((f, i) => {
                        const cellStyle = {
                            /* 숫자 크기는 그대로 둔다 — 대표가 보려는 것이 이 값이다 */
                            padding: listMode ? '11px 12px' : '14px 12px',
                            border: 'none',
                            borderLeft: i === 0 ? 'none' : `1px solid ${BORDER}`,
                            background: f.alert ? s.tint : 'transparent',
                            minWidth: 0,
                        } as const;
                        const cell = (
                            <>
                                <span
                                    style={{
                                        fontSize: 12.5,
                                        color: f.alert ? s.accent : '#93A0AE',
                                        fontWeight: f.alert ? 700 : 400,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {f.label}
                                </span>
                                <span
                                    style={{
                                        /*
                                          값은 서버 문자열이다. 숫자만 온다고 가정하지 않는다 —
                                          '등록 전' 처럼 글자로 오는 값도 있고, 서버가 근거를
                                          새로 붙이면(예: '당일 취소·변경') 칸이 더 좁아진다.
                                          긴 값은 한 급 줄여서 옆 칸을 밀어내지 않게 한다.
                                        */
                                        fontSize: f.value.length > 6 ? 19 : 26,
                                        fontWeight: 800,
                                        color: f.alert ? s.accent : '#16202E',
                                        letterSpacing: '-0.03em',
                                        marginTop: 4,
                                        lineHeight: '32px',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {f.value}
                                </span>
                            </>
                        );
                        /*
                          라벨은 서버 문자열이라 같은 값이 두 번 올 수 있다.
                          라벨만 key 로 쓰면 그때 React 가 한 칸을 지우고 다시 그린다.
                        */
                        const key = `${f.label}-${i}`;
                        /*
                          목록에서는 근거 칸이 버튼이 아니다 — 눌러도 카드가 열린다.
                          원천 데이터로 가는 길은 우측 패널의 '왜 이렇게 판단했는지 보기'
                          하나로 모은다. 숫자를 크게 보여주는 일은 그대로다.
                        */
                        return listMode ? (
                            <div
                                key={key}
                                className="flex flex-1 flex-col items-center justify-center"
                                style={cellStyle}
                            >
                                {cell}
                            </div>
                        ) : (
                            <button
                                key={key}
                                type="button"
                                onClick={() => onShowRaw(item.itemId)}
                                title="눌러서 원천 데이터 보기"
                                className="flex flex-1 flex-col items-center justify-center"
                                style={{ ...cellStyle, cursor: 'pointer' }}
                            >
                                {cell}
                            </button>
                        );
                    })}
                </div>
                )}

                {/*
                  추천 액션 — 무엇을, 왜, 뭐라고 말할지까지.

                  ⚠️ 목록에는 그리지 않는다(actions='none').
                  이 블록만으로 세로 260px 이라, 목록에 다섯 벌 깔리면 정작 대표님이
                  보려는 수치가 화면 밖으로 밀린다. 강사에게 무엇을 시킬지는
                  카드를 눌러 상세에서 정한다.
                */}
                {!listMode && (
                <div
                    className="rounded-[12px]"
                    style={{
                        marginTop: 16,
                        background: '#F7F9FC',
                        border: '1px solid #E7EDF6',
                        padding: '16px 18px',
                    }}
                >
                    {/* 할 일 — 무엇을 해야 하는지가 한 줄로 딱 떨어져야 한다 */}
                    <div className="flex items-center" style={{ gap: 8 }}>
                        <span
                            className="flex flex-none items-center justify-center rounded-[5px]"
                            style={{
                                background: '#1A305D',
                                color: '#FFFFFF',
                                fontSize: 11,
                                fontWeight: 800,
                                padding: '4px 9px',
                                letterSpacing: '0.04em',
                            }}
                        >
                            오늘 할 일
                        </span>
                        <span style={{ fontSize: 12.5, color: '#93A0AE' }}>
                            문구까지 준비해뒀습니다. 확인하고 그대로 쓰시면 됩니다
                        </span>
                    </div>

                    <div style={{ marginTop: 9 }}>
                        <span
                            style={{
                                fontSize: 19,
                                fontWeight: 700,
                                color: '#16202E',
                                lineHeight: '27px',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            {item.recommendation.headline}
                        </span>
                    </div>

                    <div style={{ marginTop: 6 }}>
                        <span style={{ fontSize: 14, color: '#7C8794' }}>
                            {item.recommendation.reason}
                        </span>
                    </div>

                    {/* 보낼 문구 초안 */}
                    <div className="flex items-center" style={{ marginTop: 14, gap: 7 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#5B6B7F' }}>
                            {outcome.toLabel}
                        </span>
                        {editing && (
                            <span style={{ fontSize: 12, color: '#3D73DD', fontWeight: 600 }}>
                                수정 중 — 고친 내용 그대로 기록됩니다
                            </span>
                        )}
                    </div>
                    {editing ? (
                        <textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            rows={4}
                            autoFocus
                            /*
                              이 초안은 **회원에게 보낼 글**이다 — 지시로 나갈 때
                              memberDraft 로 실린다 (OpsBriefingTodayPage.handleAction).
                              서버 상한이 1000자다. 넘기면 400 이 난다.
                            */
                            maxLength={1000}
                            style={{
                                marginTop: 7,
                                width: '100%',
                                background: '#FFFFFF',
                                border: '1px solid #B9CBE6',
                                borderLeft: `3px solid ${s.rail}`,
                                borderRadius: 9,
                                padding: '12px 14px',
                                fontSize: 14.5,
                                color: '#3A4757',
                                lineHeight: '22px',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                                outline: 'none',
                            }}
                        />
                    ) : (
                        <div
                            className="rounded-[9px]"
                            style={{
                                marginTop: 7,
                                background: '#FFFFFF',
                                border: '1px solid #E3EAF3',
                                padding: '12px 14px',
                                borderLeft: `3px solid ${s.rail}`,
                                whiteSpace: 'pre-line',
                            }}
                        >
                            <span style={{ fontSize: 14.5, color: '#3A4757', lineHeight: '22px' }}>
                                “{draft}”
                            </span>
                        </div>
                    )}

                    {/*
                      실행 버튼.
                      예전에는 42px 버튼 세 개가 나란히 있어서 무엇이 '진짜 할 일'인지 흐렸다.
                      주 버튼 하나만 크게 세우고, 누르면 무슨 일이 생기는지 바로 아래 적는다.
                    */}
                    <div style={{ marginTop: 16 }}>
                        {/*
                          이미 나가 있는 지시가 있으면 버튼 위에서 먼저 말한다.
                          서버는 중복을 막지 않는다 — 인덱스가 unique 가 아니다.
                          여기서 안 알리면 같은 회원 건이 강사 화면에 두 줄로 쌓인다.
                        */}
                        {sentOrder && (
                            <div
                                className="rounded-[9px]"
                                style={{
                                    marginBottom: 10,
                                    background: '#F4FCF9',
                                    border: '1px solid #B7DFD2',
                                    padding: '9px 12px',
                                }}
                            >
                                <span style={{ fontSize: 13, color: '#00795F', lineHeight: '19px' }}>
                                    ✓ {sentOrder.sentLabel}에 {sentOrder.coachName} 강사에게 지시하셨습니다 ·
                                    또 보내면 같은 건이 하나 더 생깁니다
                                </span>
                            </div>
                        )}
                        {/*
                          오늘 이 회원에게 보낸 선물.
                          ⚠️ 새로고침하면 사라진다 — 서버 발송 내역에 수신자 id 가 없어 복원할 수 없다.
                             이중 발송은 멱등키가 서버에서 막는다. 이 줄은 그 사실을 알려 주는 표시일 뿐이다.
                        */}
                        {sentGift && (
                            <div
                                className="rounded-[9px]"
                                style={{
                                    marginBottom: 10,
                                    background: '#FFFBEB',
                                    border: '1px solid #FDE68A',
                                    padding: '9px 12px',
                                }}
                            >
                                <span style={{ fontSize: 13, color: '#92400E', lineHeight: '19px' }}>
                                    🎁 오늘 이 회원에게 {sentGift}
                                </span>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => onAction(item.itemId, item.recommendation.actionCode, draft)}
                            /*
                              서버 왕복 중에는 잠근다.
                              지시 생성은 멱등이 아니라, 반응이 없다고 한 번 더 누르면
                              같은 회원 건이 강사 화면에 두 줄로 뜬다.
                            */
                            disabled={busy}
                            className="flex w-full items-center justify-center rounded-[11px] transition-all"
                            style={{
                                height: 54,
                                cursor: busy ? 'progress' : 'pointer',
                                fontSize: 17,
                                fontWeight: 700,
                                background: busy ? '#7C8794' : '#1A305D',
                                color: '#FFFFFF',
                                border: 'none',
                                boxShadow: busy ? 'none' : '0 6px 16px rgba(26,48,93,0.22)',
                                gap: 9,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            {busy
                                ? '처리 중…'
                                : editing
                                  ? '고친 내용으로 전달하기'
                                  : item.recommendation.actionLabel}
                            {!busy &&
                                (() => {
                                    const b = buttonValueOf(item.value);
                                    if (!b) return null;
                                    return (
                                        <span
                                            className="rounded-[6px]"
                                            style={{
                                                background: 'rgba(255,255,255,0.16)',
                                                fontSize: 14,
                                                fontWeight: 700,
                                                padding: '4px 10px',
                                            }}
                                        >
                                            {b.text} {b.word}
                                        </span>
                                    );
                                })()}
                        </button>

                        {/* 누르면 무슨 일이 생기는지 — 이걸 몰라서 안 누른다 */}
                        <span
                            style={{
                                fontSize: 12.5,
                                color: '#7C8794',
                                display: 'block',
                                textAlign: 'center',
                                marginTop: 9,
                                lineHeight: '18px',
                            }}
                        >
                            {outcome.effect}
                        </span>

                        {/* 대안 — 주 버튼과 확실히 급을 나눈다 */}
                        <div className="flex items-center" style={{ marginTop: 13, gap: 8 }}>
                            <button
                                type="button"
                                onClick={() => setEditing((v) => !v)}
                                className="rounded-[9px]"
                                style={{
                                    flex: 1,
                                    height: 42,
                                    cursor: 'pointer',
                                    fontSize: 14.5,
                                    fontWeight: 600,
                                    background: editing ? '#EEF3FB' : '#FFFFFF',
                                    color: '#425063',
                                    border: `1px solid ${editing ? '#B9CBE6' : '#DCE4EE'}`,
                                }}
                            >
                                {editing ? '수정 취소' : '✏️ 문구 고치기'}
                            </button>
                            {item.actions
                                .filter((a) => a.code !== item.recommendation.actionCode)
                                /* 선물은 보낼 수 없는 계정에서 아예 그리지 않는다 (누르면 403 이다) */
                                .filter((a) => !(a.code === 'GIFT' && giftGate?.hidden))
                                .map((a) => {
                                    /* 선물만 조건이 다르다 — 지난 날짜에서는 잠근다 */
                                    const gated = a.code === 'GIFT' && !!giftGate?.disabled;
                                    const disabled = busy || gated;
                                    return (
                                        <button
                                            key={a.code}
                                            type="button"
                                            onClick={() => onAction(item.itemId, a.code, draft)}
                                            disabled={disabled}
                                            title={gated ? giftGate?.reason : undefined}
                                            className="rounded-[9px]"
                                            style={{
                                                flex: 1,
                                                height: 42,
                                                cursor: gated ? 'not-allowed' : busy ? 'progress' : 'pointer',
                                                fontSize: 14.5,
                                                fontWeight: 600,
                                                background: gated ? '#F4F6FA' : '#FFFFFF',
                                                color: gated ? '#93A0AE' : '#425063',
                                                border: '1px solid #DCE4EE',
                                            }}
                                        >
                                            {a.label}
                                        </button>
                                    );
                                })}
                        </div>
                    </div>
                </div>
                )}

                {/*
                  「이 판단 틀렸어요」와 「넘어가기」 버튼이 있었다.
                  둘 다 로컬 state 에서 카드를 지우고 토스트만 띄웠다 —
                  "3일 후 다시 알려드릴게요", "판단 기준을 조정할게요" 라고 말해 놓고
                  새로고침하면 그대로 돌아왔고, 서버에는 아무것도 남지 않았다.
                  대응 API(스누즈 · 오판 신고)가 열리면 되살린다.
                  BACKEND_HANDOFF_OPS_BRIEFING_ACTIONS.md 참고.
                */}
            </div>

            {/*
              우측 지표 패널 — 숫자를 확실히 보이게.

              목록에서는 패널 전체를 누르는 영역으로 두지 않는다. 카드가 이미
              상세로 가는 문이라, 여기까지 원천 데이터로 새면 같은 카드에서
              누르는 자리마다 다른 화면이 열린다. 아래 근거 버튼은 두 모드 모두
              살아 있다 — 판단을 믿게 만드는 것이 이 화면의 핵심이다.

              ─────────────────────────────────────────────────────────────
              목록에서는 이 패널을 반높이로 접는다
              ─────────────────────────────────────────────────────────────
              큰 세로 배치(34px 숫자 + 8주 추이 + 46px 버튼 + 두 줄 설명)는
              세로 340px 이라 **패널이 카드 높이를 혼자 끌고 갔다.** 왼쪽은
              제목 + 수치 스트립에서 끝나는데 오른쪽이 더 길어서, 카드 왼쪽
              아래가 통째로 빈칸으로 남았다 — 대표 지적 사항이다.

              내용도 왼쪽 스트립과 겹친다(최근 60일·이전 60일은 이미 스트립에
              큰 숫자로 서 있다). 그래서 목록에서는 **변화 한 줄 + 추이 + 근거로
              가는 링크**만 남기고, 기간 라벨·설명 문구·부가 지표는 상세로 미룬다.
            */}
            {listMode ? (
                <div
                    className="flex flex-none flex-col justify-center"
                    style={{
                        width: 236,
                        padding: '14px 18px',
                        borderLeft: '1px solid #EDF1F7',
                        background: '#FBFCFE',
                    }}
                >
                    {/* 무슨 값인지 — 숫자보다 먼저 읽혀야 한다 */}
                    <span
                        style={{
                            fontSize: 12.5,
                            color: '#5B6B7F',
                            fontWeight: 700,
                            lineHeight: '17px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {item.keyMetric.label}
                    </span>

                    {/*
                      이전 → 현재를 한 줄로.
                      기간 라벨('이전 60일'/'최근 60일')은 여기서 뺀다 — 왼쪽 스트립이
                      이미 같은 말을 하고 있어서, 두 번 적으면 자리만 먹는다.
                    */}
                    <div className="flex items-baseline" style={{ marginTop: 5, gap: 6 }}>
                        <span
                            style={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: '#A7B0BC',
                                letterSpacing: '-0.02em',
                                whiteSpace: 'nowrap',
                            }}
                            title={beforeCovered ? item.keyMetric.beforeLabel : '이 구간은 회원 등록 전입니다'}
                        >
                            {beforeValueOf(item.keyMetric)}
                        </span>
                        <span style={{ fontSize: 13, color: '#C4CCD8' }}>→</span>
                        <span
                            style={{
                                fontSize: 22,
                                fontWeight: 800,
                                lineHeight: '26px',
                                letterSpacing: '-0.03em',
                                color: item.keyMetric.alert ? s.accent : '#16202E',
                                whiteSpace: 'nowrap',
                            }}
                            title={item.keyMetric.afterLabel}
                        >
                            {item.keyMetric.afterValue}
                        </span>
                        {/*
                          변화율 배지. 목록에서는 '감소/증가' 글자를 떼고 부호와
                          숫자만 남긴다 — ▼ 가 이미 방향을 말한다.
                        */}
                        {beforeCovered ? (
                            <span
                                className="ml-auto inline-flex items-center rounded-[6px]"
                                style={{
                                    background: item.keyMetric.alert ? s.badgeBg : '#E9F6F2',
                                    color: item.keyMetric.alert ? s.accent : '#00A793',
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    padding: '3px 8px',
                                    whiteSpace: 'nowrap',
                                    flex: 'none',
                                }}
                                title={`${item.keyMetric.beforeLabel} 대비 ${Math.abs(item.keyMetric.deltaPct)}% ${
                                    item.keyMetric.deltaPct < 0 ? '감소' : '증가'
                                }`}
                            >
                                {item.keyMetric.deltaPct < 0 ? '▼' : '▲'}{' '}
                                {Math.abs(item.keyMetric.deltaPct)}%
                            </span>
                        ) : (
                            /* 등록 전 구간과 비교한 "▼100%" 는 근거가 없는 숫자다 */
                            <span
                                className="ml-auto inline-flex items-center rounded-[6px]"
                                style={{
                                    background: '#F1F4F9',
                                    color: '#7C8794',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    padding: '3px 8px',
                                    whiteSpace: 'nowrap',
                                    flex: 'none',
                                }}
                                title="이 구간은 회원 등록 전이라 비교가 성립하지 않습니다"
                            >
                                {BEFORE_UNCOVERED_DELTA_TEXT}
                            </span>
                        )}
                    </div>

                    {/* 8주 추이 — 남기되 반높이로 */}
                    <div style={{ marginTop: 9 }}>
                        <TrendBars
                            data={item.keyMetric.trend}
                            color={item.keyMetric.alert ? s.accent : '#3D73DD'}
                            alert={item.keyMetric.alert}
                            compact
                        />
                    </div>

                    {/*
                      근거로 가는 길. 목록에서는 46px 버튼이 아니라 텍스트 링크다 —
                      카드를 눌러도 같은 근거를 볼 수 있는 상세가 열린다.
                      다만 길 자체를 없애지는 않는다(판단을 믿게 만드는 자리라서).
                    */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onShowRaw(item.itemId);
                        }}
                        className="flex items-center"
                        style={{
                            marginTop: 10,
                            alignSelf: 'flex-start',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            color: '#20406F',
                            fontSize: 12.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                            gap: 4,
                        }}
                    >
                        🔍 왜 이렇게 판단했는지 보기
                        <span style={{ fontSize: 13, opacity: 0.6 }}>›</span>
                    </button>
                </div>
            ) : (
            <div
                className="flex flex-none flex-col justify-center"
                role="button"
                tabIndex={0}
                onClick={() => onShowRaw(item.itemId)}
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') onShowRaw(item.itemId);
                }}
                title="원천 데이터 보기"
                style={{
                    width: 268,
                    padding: '20px 24px',
                    borderLeft: '1px solid #EDF1F7',
                    background: '#FBFCFE',
                    cursor: 'pointer',
                }}
            >
                {/*
                  패널 제목.

                  예전에는 12.5px 회색이라 아래 '이전 60일 / 최근 60일' 라벨과 급이 같아 보였고,
                  원장님이 이 칸을 **"두 기간의 전체 수업 수"** 로 읽었습니다.
                  실제로는 '주당 수업 횟수' 처럼 서버가 정한 지표 이름이라,
                  무슨 값인지가 숫자보다 먼저 읽혀야 합니다. 제목을 키우고 기간 라벨을
                  한 급 낮춰서 위계를 분명히 합니다 — 제목 > 숫자 > 기간 라벨.
                */}
                <span
                    style={{
                        fontSize: 15,
                        color: '#16202E',
                        letterSpacing: '-0.01em',
                        fontWeight: 700,
                        lineHeight: '21px',
                    }}
                >
                    {item.keyMetric.label}
                </span>

                {/* 이전 → 현재 대비. 단일 값보다 변화가 즉시 읽힌다 */}
                <div className="flex items-end" style={{ marginTop: 12, gap: 10 }}>
                    <div className="flex flex-col">
                        <span style={{ fontSize: 12, color: '#A7B0BC' }}>
                            {item.keyMetric.beforeLabel}
                        </span>
                        <span
                            style={{
                                fontSize: beforeCovered ? 21 : 17,
                                fontWeight: 700,
                                color: '#A7B0BC',
                                lineHeight: '26px',
                                letterSpacing: '-0.02em',
                                whiteSpace: 'nowrap',
                            }}
                            /*
                              이전 구간이 등록 전이면 '0회' 가 아니라 '등록 전' 이다.
                              0 으로 그리면 두 달 전 등록한 신규 회원이 "수업 100% 감소" 로
                              뜨고, 원장님이 멀쩡한 회원에게 이탈 방어 연락을 건다.
                              서버가 이 사실을 안 보내면 예전 그대로 그린다.
                            */
                            title={beforeCovered ? undefined : '이 구간은 회원 등록 전입니다'}
                        >
                            {beforeValueOf(item.keyMetric)}
                        </span>
                    </div>

                    <span style={{ fontSize: 17, color: '#C4CCD8', paddingBottom: 3 }}>→</span>

                    <div className="flex flex-col">
                        <span style={{ fontSize: 12, color: '#93A0AE' }}>
                            {item.keyMetric.afterLabel}
                        </span>
                        <span
                            style={{
                                fontSize: 34,
                                fontWeight: 800,
                                lineHeight: '38px',
                                letterSpacing: '-0.03em',
                                color: item.keyMetric.alert ? s.accent : '#16202E',
                            }}
                        >
                            {item.keyMetric.afterValue}
                        </span>
                    </div>
                </div>

                {/* 변화율 배지 */}
                <div style={{ marginTop: 10 }}>
                    {beforeCovered ? (
                        <span
                            className="inline-flex items-center rounded-[7px]"
                            style={{
                                background: item.keyMetric.alert ? s.badgeBg : '#E9F6F2',
                                color: item.keyMetric.alert ? s.accent : '#00A793',
                                fontSize: 14,
                                fontWeight: 800,
                                padding: '5px 11px',
                                gap: 4,
                            }}
                        >
                            {item.keyMetric.deltaPct < 0 ? '▼' : '▲'}{' '}
                            {Math.abs(item.keyMetric.deltaPct)}%{' '}
                            <span style={{ fontWeight: 600, fontSize: 13 }}>
                                {item.keyMetric.deltaPct < 0 ? '감소' : '증가'}
                            </span>
                        </span>
                    ) : (
                        /*
                          등록 전 구간과 비교한 "▼100% 감소" 는 근거가 없는 숫자다.
                          비교가 성립하지 않으면 배지를 만들지 않고 그 사실을 적는다.
                        */
                        <span
                            className="inline-flex items-center rounded-[7px]"
                            style={{
                                background: '#F1F4F9',
                                color: '#7C8794',
                                fontSize: 13,
                                fontWeight: 700,
                                padding: '5px 11px',
                            }}
                        >
                            {BEFORE_UNCOVERED_DELTA_TEXT} · 등록 전 구간
                        </span>
                    )}
                </div>

                {/* 주차별 추이 */}
                <div style={{ marginTop: 14 }}>
                    <TrendBars
                        data={item.keyMetric.trend}
                        color={item.keyMetric.alert ? s.accent : '#3D73DD'}
                        alert={item.keyMetric.alert}
                    />
                </div>

                {/*
                  근거 열람 버튼.
                  예전에는 밑줄 친 12px 글씨였는데, 그러면 아무도 누르지 않는다.
                  판단을 믿게 만드는 게 이 화면의 핵심이라 진짜 버튼 크기로 키운다.
                */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onShowRaw(item.itemId);
                    }}
                    className="flex w-full items-center justify-center rounded-[10px]"
                    style={{
                        marginTop: 16,
                        height: 46,
                        background: '#EEF3FB',
                        border: '1px solid #B9CBE6',
                        color: '#20406F',
                        fontSize: 15,
                        fontWeight: 700,
                        cursor: 'pointer',
                        gap: 7,
                    }}
                >
                    🔍 왜 이렇게 판단했는지 보기
                    <span style={{ fontSize: 16, opacity: 0.6 }}>›</span>
                </button>
                <span
                    style={{
                        fontSize: 12,
                        color: '#93A0AE',
                        marginTop: 7,
                        display: 'block',
                        textAlign: 'center',
                        lineHeight: '17px',
                    }}
                >
                    {/*
                      예전에는 "근거 3가지(칭찬은 4가지)"라고 개수를 적어 뒀다.
                      근거는 서버가 항목마다 다른 개수로 내려주므로 화면이 셀 수 없는 값이다.
                      근거 화면에서 개수를 밝히는 것이 이 화면의 존재 이유인데,
                      그 자리에 지어낸 숫자를 적으면 안 된다.
                    */}
                    실제 수업 기록과 비교 기준을
                    <br />
                    항목별로 보여드립니다
                </span>

                {item.subMetrics && item.subMetrics.length > 0 && (
                    <div
                        className="flex flex-col"
                        style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #EDF1F7', gap: 7 }}
                    >
                        {item.subMetrics.map((m, i) => (
                            <div key={i} className="flex items-center justify-between">
                                <span style={{ fontSize: 13.5, color: '#93A0AE' }}>{m.label}</span>
                                <span
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 700,
                                        color: m.alert ? s.accent : '#2C3A4B',
                                    }}
                                >
                                    {m.value}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default BriefingCard;
